/**
 * useMeState — signed-in user state (vendor↔member context + role
 * flags), backed by /api/me/state. Silently returns null while
 * loading / for anonymous visitors.
 *
 * Exposes:
 *   state    — { has_vendor_profile, has_member_profile, active_context, ... }
 *   loading  — true until first fetch resolves
 *   setActive(active)  — optimistic switch + POST /api/me/context
 *   refresh() — re-fetch
 */
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export function useMeState() {
  const { user, checking } = useAuth();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!user) { setState(null); setLoading(false); return; }
    try {
      const { data } = await api.get("/me/state");
      setState(data || null);
    } catch (_e) {
      setState(null);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (checking) return;
    setLoading(true);
    refresh();
  }, [checking, refresh]);

  const setActive = useCallback(async (active) => {
    if (!state || state.active_context === active) return state;
    const prev = state;
    setState({ ...state, active_context: active });
    try {
      await api.post("/me/context", { active });
      return { ...prev, active_context: active };
    } catch (e) {
      setState(prev);
      throw e;
    }
  }, [state]);

  return { state, loading, refresh, setActive };
}
